import React, { useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    LinearProgress,
    Chip,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Tabs,
    Tab,
    Alert,
    CircularProgress,
    Divider,
} from '@mui/material';
import { ExpandMore, Assessment, CheckCircle, School } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { ReadingAttempt } from '@services/reading/types';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => (
    <div
        role="tabpanel"
        hidden={value !== index}
        id={`feedback-tabpanel-${index}`}
        aria-labelledby={`feedback-tab-${index}`}
        {...other}
    >
        {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
);

interface DetailedSpeechFeedbackProps {
    attempt: ReadingAttempt;
    onClose?: () => void;
}

const DetailedSpeechFeedback: React.FC<DetailedSpeechFeedbackProps> = ({ attempt, onClose }) => {
    const { t } = useTranslation();
    const [currentTab, setCurrentTab] = useState(0);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setCurrentTab(newValue);
    };

    // Extract standardized test scores
    const testScores = attempt.speech_analysis_response?.text_score;

    const getScoreColor = (score: number) => {
        if (score >= 80) return 'success';
        if (score >= 60) return 'warning';
        return 'error';
    };

    const getScoreLabel = (score: number) => {
        if (score >= 90) return t('studentReading.speechAnalysis.scores.excellent');
        if (score >= 80) return t('studentReading.speechAnalysis.scores.veryGood');
        if (score >= 70) return t('studentReading.speechAnalysis.scores.good');
        if (score >= 60) return t('studentReading.speechAnalysis.scores.fair');
        if (score >= 50) return t('studentReading.speechAnalysis.scores.needsImprovement');
        return t('studentReading.speechAnalysis.scores.poor');
    };

    const renderOverviewTab = () => (
        <Grid container spacing={3}>
            {/* Overall Scores */}
            <Grid item xs={12} md={6}>
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                            <Assessment color="primary" />
                            {t('studentReading.speechAnalysis.overview.overallPerformance')}
                        </Typography>

                        <Box mb={2}>
                            <Box display="flex" justifyContent="space-between" mb={1}>
                                <Typography>{t('studentReading.practice.interface.pronunciation')}</Typography>
                                <Typography fontWeight="bold">{Math.round(attempt.pronunciation_score)}%</Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={attempt.pronunciation_score}
                                color={getScoreColor(attempt.pronunciation_score) as any}
                                sx={{ height: 8, borderRadius: 4 }}
                            />
                            <Typography variant="caption" color="text.secondary">
                                {getScoreLabel(attempt.pronunciation_score)}
                            </Typography>
                        </Box>

                        <Box mb={2}>
                            <Box display="flex" justifyContent="space-between" mb={1}>
                                <Typography>{t('studentReading.practice.interface.fluency')}</Typography>
                                <Typography fontWeight="bold">{Math.round(attempt.fluency_score)}%</Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={attempt.fluency_score}
                                color={getScoreColor(attempt.fluency_score) as any}
                                sx={{ height: 8, borderRadius: 4 }}
                            />
                            <Typography variant="caption" color="text.secondary">
                                {getScoreLabel(attempt.fluency_score)}
                            </Typography>
                        </Box>

                        <Box mb={2}>
                            <Box display="flex" justifyContent="space-between" mb={1}>
                                <Typography>{t('studentReading.practice.interface.intonation')}</Typography>
                                <Typography fontWeight="bold">{Math.round(attempt.intonation_score)}%</Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={attempt.intonation_score}
                                color={getScoreColor(attempt.intonation_score) as any}
                                sx={{ height: 8, borderRadius: 4 }}
                            />
                            <Typography variant="caption" color="text.secondary">
                                {getScoreLabel(attempt.intonation_score)}
                            </Typography>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Box textAlign="center">
                            <CircularProgress
                                variant="determinate"
                                value={attempt.overall_score}
                                size={80}
                                thickness={6}
                                color={getScoreColor(attempt.overall_score) as any}
                            />
                            <Typography variant="h4" fontWeight="bold" color="primary">
                                {Math.round(attempt.overall_score)}%
                            </Typography>
                            <Typography variant="subtitle2" color="text.secondary">
                                {t('studentReading.practice.interface.overallScore')}
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            </Grid>

            {/* Standardized Test Scores */}
            <Grid item xs={12} md={6}>
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                            <School color="primary" />
                            {t('studentReading.speechAnalysis.overview.testEquivalencies')}
                        </Typography>

                        {attempt.ielts_pronunciation_score && (
                            <Box mb={2}>
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <Typography>{t('studentReading.practice.interface.ielts')} Band</Typography>
                                    <Chip
                                        label={`Band ${attempt.ielts_pronunciation_score}`}
                                        color={
                                            attempt.ielts_pronunciation_score >= 7
                                                ? 'success'
                                                : attempt.ielts_pronunciation_score >= 6
                                                ? 'warning'
                                                : 'error'
                                        }
                                    />
                                </Box>
                            </Box>
                        )}

                        {attempt.pte_pronunciation_score && (
                            <Box mb={2}>
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <Typography>{t('studentReading.practice.interface.pte')} Score</Typography>
                                    <Chip
                                        label={attempt.pte_pronunciation_score}
                                        color={
                                            attempt.pte_pronunciation_score >= 65
                                                ? 'success'
                                                : attempt.pte_pronunciation_score >= 50
                                                ? 'warning'
                                                : 'error'
                                        }
                                    />
                                </Box>
                            </Box>
                        )}

                        {attempt.toeic_pronunciation_score && (
                            <Box mb={2}>
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <Typography>{t('studentReading.practice.interface.toeic')} Score</Typography>
                                    <Chip
                                        label={attempt.toeic_pronunciation_score}
                                        color={
                                            attempt.toeic_pronunciation_score >= 60
                                                ? 'success'
                                                : attempt.toeic_pronunciation_score >= 40
                                                ? 'warning'
                                                : 'error'
                                        }
                                    />
                                </Box>
                            </Box>
                        )}

                        {attempt.cefr_pronunciation_level && (
                            <Box mb={2}>
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <Typography>{t('studentReading.practice.interface.cefr')} Level</Typography>
                                    <Chip
                                        label={attempt.cefr_pronunciation_level}
                                        color={
                                            ['C1', 'C2'].includes(attempt.cefr_pronunciation_level)
                                                ? 'success'
                                                : ['B1', 'B2'].includes(attempt.cefr_pronunciation_level)
                                                ? 'warning'
                                                : 'error'
                                        }
                                    />
                                </Box>
                            </Box>
                        )}

                        {attempt.speechace_pronunciation_score && (
                            <Box mb={2}>
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <Typography>{t('studentReading.practice.interface.speechAce')} Score</Typography>
                                    <Chip
                                        label={attempt.speechace_pronunciation_score}
                                        color={
                                            attempt.speechace_pronunciation_score >= 70
                                                ? 'success'
                                                : attempt.speechace_pronunciation_score >= 50
                                                ? 'warning'
                                                : 'error'
                                        }
                                    />
                                </Box>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </Grid>

            {/* Key Insights */}
            <Grid item xs={12}>
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            {t('studentReading.speechAnalysis.overview.keyInsights')}
                        </Typography>

                        <Grid container spacing={2}>
                            {attempt.errors_detected.map((error, index) => (
                                <Grid item xs={12} sm={6} key={index}>
                                    <Alert severity={error.severity === 'high' ? 'error' : 'warning'} sx={{ mb: 1 }}>
                                        <Typography variant="subtitle2" fontWeight="bold">
                                            {error.word}: {error.phoneme}
                                        </Typography>
                                        <Typography variant="body2">{error.suggestion}</Typography>
                                    </Alert>
                                </Grid>
                            ))}

                            {attempt.errors_detected.length === 0 && (
                                <Grid item xs={12}>
                                    <Alert severity="success" icon={<CheckCircle />}>
                                        {t('studentReading.practice.feedback.greatPronunciation')}
                                    </Alert>
                                </Grid>
                            )}
                        </Grid>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );

    const renderWordAnalysisTab = () => (
        <Box>
            <Typography variant="h6" gutterBottom>
                {t('studentReading.speechAnalysis.wordAnalysis.title')}
            </Typography>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('studentReading.speechAnalysis.wordAnalysis.headers.word')}</TableCell>
                            <TableCell align="center">
                                {t('studentReading.speechAnalysis.wordAnalysis.headers.qualityScore')}
                            </TableCell>
                            <TableCell align="center">
                                {t('studentReading.speechAnalysis.wordAnalysis.headers.phonemes')}
                            </TableCell>
                            <TableCell align="center">
                                {t('studentReading.speechAnalysis.wordAnalysis.headers.syllables')}
                            </TableCell>
                            <TableCell>{t('studentReading.speechAnalysis.wordAnalysis.headers.details')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {attempt.word_scores.map((word, index) => (
                            <TableRow key={index}>
                                <TableCell>
                                    <Typography fontWeight="bold">{word.word}</Typography>
                                </TableCell>
                                <TableCell align="center">
                                    <Chip
                                        label={`${word.quality_score}%`}
                                        color={getScoreColor(word.quality_score) as any}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell align="center">{word.phone_score_list?.length}</TableCell>
                                <TableCell align="center">{word.syllable_score_list?.length}</TableCell>
                                <TableCell>
                                    <Accordion>
                                        <AccordionSummary expandIcon={<ExpandMore />}>
                                            <Typography variant="body2">
                                                {t('studentReading.speechAnalysis.wordAnalysis.viewDetails')}
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            {/* Phoneme details */}
                                            <Typography variant="subtitle2" gutterBottom>
                                                {t('studentReading.speechAnalysis.wordAnalysis.phonemeAnalysis')}
                                            </Typography>
                                            <Box mb={2}>
                                                {word.phone_score_list?.map((phone, phoneIndex) => (
                                                    <Chip
                                                        key={phoneIndex}
                                                        label={`${phone.phone}: ${phone.quality_score}%`}
                                                        size="small"
                                                        color={getScoreColor(phone.quality_score) as any}
                                                        sx={{ mr: 0.5, mb: 0.5 }}
                                                    />
                                                ))}
                                            </Box>

                                            {/* Syllable details */}
                                            <Typography variant="subtitle2" gutterBottom>
                                                {t('studentReading.speechAnalysis.wordAnalysis.syllableAnalysis')}
                                            </Typography>
                                            <Box>
                                                {word.syllable_score_list?.map((syllable, syllIndex) => (
                                                    <Chip
                                                        key={syllIndex}
                                                        label={`${syllable.letters}: ${syllable.quality_score}%`}
                                                        size="small"
                                                        color={getScoreColor(syllable.quality_score) as any}
                                                        sx={{ mr: 0.5, mb: 0.5 }}
                                                    />
                                                ))}
                                            </Box>
                                        </AccordionDetails>
                                    </Accordion>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );

    const renderPhonemeAnalysisTab = () => (
        <Box>
            <Typography variant="h6" gutterBottom>
                {t('studentReading.speechAnalysis.phonemeAnalysis.title')}
            </Typography>

            <Grid container spacing={2}>
                {attempt.phoneme_scores.map((phone, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                        <Card>
                            <CardContent>
                                <Typography variant="h5" align="center" gutterBottom>
                                    /{phone.phone}/
                                </Typography>
                                <Box textAlign="center" mb={2}>
                                    <CircularProgress
                                        variant="determinate"
                                        value={phone.quality_score}
                                        color={getScoreColor(phone.quality_score) as any}
                                        size={60}
                                    />
                                    <Typography variant="h6" fontWeight="bold">
                                        {phone.quality_score}%
                                    </Typography>
                                </Box>

                                {phone.sound_most_like && phone.sound_most_like !== phone.phone && (
                                    <Alert severity="warning" sx={{ size: 'small' }}>
                                        {t('studentReading.speechAnalysis.phonemeAnalysis.soundsLike')} /
                                        {phone.sound_most_like}/
                                    </Alert>
                                )}

                                <Box mt={1}>
                                    <Typography variant="caption" color="text.secondary">
                                        {t('studentReading.speechAnalysis.phonemeAnalysis.position')} {phone.extent[0]}-
                                        {phone.extent[1]}ms
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );

    const renderTechnicalTab = () => (
        <Box>
            <Typography variant="h6" gutterBottom>
                {t('studentReading.speechAnalysis.technical.title')}
            </Typography>

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="subtitle1" gutterBottom>
                                {t('studentReading.speechAnalysis.technical.audioInfo')}
                            </Typography>
                            <Box>
                                <Typography variant="body2">
                                    <strong>{t('studentReading.speechAnalysis.technical.duration')}:</strong>{' '}
                                    {attempt.audio_duration}s
                                </Typography>
                                <Typography variant="body2">
                                    <strong>{t('studentReading.speechAnalysis.technical.format')}:</strong>{' '}
                                    {attempt.audio_format}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>{t('studentReading.speechAnalysis.technical.fileSize')}:</strong>{' '}
                                    {(attempt.audio_file_size / 1024).toFixed(1)} KB
                                </Typography>
                                <Typography variant="body2">
                                    <strong>{t('studentReading.speechAnalysis.technical.transcribedText')}:</strong>{' '}
                                    {attempt.speech_to_text_result}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="subtitle1" gutterBottom>
                                {t('studentReading.speechAnalysis.technical.apiInfo')}
                            </Typography>
                            <Box>
                                <Typography variant="body2">
                                    <strong>{t('studentReading.speechAnalysis.technical.apiVersion')}:</strong>{' '}
                                    {attempt.api_version}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>{t('studentReading.speechAnalysis.technical.responseTime')}:</strong>{' '}
                                    {new Date(attempt.api_response_timestamp).toLocaleString()}
                                </Typography>
                                {attempt.api_quota_remaining !== null && (
                                    <Typography variant="body2">
                                        <strong>{t('studentReading.speechAnalysis.technical.quotaRemaining')}:</strong>{' '}
                                        {attempt.api_quota_remaining}
                                    </Typography>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Typography variant="subtitle1" gutterBottom>
                                {t('studentReading.speechAnalysis.technical.rawData')}
                            </Typography>
                            <Box
                                component="pre"
                                sx={{
                                    backgroundColor: 'grey.100',
                                    p: 2,
                                    borderRadius: 1,
                                    maxHeight: 400,
                                    overflow: 'auto',
                                    fontSize: '0.75rem',
                                }}
                            >
                                {JSON.stringify(attempt.speech_analysis_response, null, 2)}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );

    return (
        <Box>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={currentTab} onChange={handleTabChange}>
                    <Tab label={t('studentReading.speechAnalysis.tabs.overview')} />
                    <Tab label={t('studentReading.speechAnalysis.tabs.wordAnalysis')} />
                    <Tab label={t('studentReading.speechAnalysis.tabs.phonemes')} />
                    <Tab label={t('studentReading.speechAnalysis.tabs.technical')} />
                </Tabs>
            </Box>

            <TabPanel value={currentTab} index={0}>
                {renderOverviewTab()}
            </TabPanel>

            <TabPanel value={currentTab} index={1}>
                {renderWordAnalysisTab()}
            </TabPanel>

            <TabPanel value={currentTab} index={2}>
                {renderPhonemeAnalysisTab()}
            </TabPanel>

            <TabPanel value={currentTab} index={3}>
                {renderTechnicalTab()}
            </TabPanel>
        </Box>
    );
};

export default DetailedSpeechFeedback;
